import { StandardProps } from "@/types/types";
import { FoundItLink } from "@/types/foundIt";
import { useRouter } from "next/router";
import classNames from "classnames";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { Carousel } from "@/components/Carousel/Carousel";
import { useMemo } from "react";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { useRouteParams } from "@/hooks/useRouteParams";
import styles from "./FoundItCarousel.module.scss";
import { FOUNDIT_CLICK } from "@/utils/events";
import { useFeatureFlags } from "@/flags/flags";
import { validateQueryParams } from "./utils";
import { sanitiseQueryParams } from "@/utils/queryUtils.ts";

type FoundItProps = Pick<StandardProps, "configProps" | "foundItData">;

const MIN_LINKS_TO_SHOW = 4;
const MAX_LINKS_TO_SHOW = 12;

export function FoundItCarousel({ foundItData, configProps }: FoundItProps) {
  const carouselFoundItData = foundItData.carousel;
  const router = useRouter();
  const {
    isFoundItFlagOn,
    isFoundItExperimentFlagOn,
    isRequestAnimationFrameOn,
  } = useFeatureFlags();

  const { pageType, queryWithoutPageOrChunk } = useRouteParams();

  const imageLimit = (configProps?.imageLimit as number) || MAX_LINKS_TO_SHOW;

  const greyBackground = configProps?.background === "grey";

  const fullWidth = configProps?.fullWidth;

  const links: FoundItLink[] = useMemo(() => {
    return carouselFoundItData.slice(0, imageLimit) || [];
  }, [carouselFoundItData, imageLimit]);

  if (
    !isFoundItFlagOn?.value ||
    !carouselFoundItData ||
    carouselFoundItData.length < MIN_LINKS_TO_SHOW
  ) {
    return null;
  }

  const buildFoundItNavigation = (link: FoundItLink) => {
    // append any existing query params from the DestinationUrl
    const targetUrl = new URL(link.DestinationUrl);
    const targetSearchParams = new URLSearchParams(targetUrl.search);

    const queryParams = validateQueryParams({
      ...Object.fromEntries(targetSearchParams),
      ...queryWithoutPageOrChunk,
      // append fi=c parameter for FoundIt analytics
      fi: "c",
      // append fic=true curation experiment parameter
      ...(isFoundItExperimentFlagOn?.value &&
        targetUrl.pathname.includes("/search/") && { fic: "true" }),
    });

    return {
      pathname: `${targetUrl.pathname}`,
      trackedHref: `${targetUrl.pathname}?${sanitiseQueryParams(queryParams, { retainFoundItTracking: true })}`,
      trackedQueryParams: queryParams,
    };
  };

  const onFoundItClick = (
    evt: React.MouseEvent<Element>,
    link: FoundItLink,
  ) => {
    evt.preventDefault();
    const { Order, AnchorText, DestinationUrl } = link;
    const navigation = buildFoundItNavigation(link);

    dispatchLegacyAnalyticsEvent(FOUNDIT_CLICK, {
      action: "foundit_click",
      foundItParam: `${router.asPath}|${pageType}|main-links|${Order}|${links.length}|${AnchorText}|${DestinationUrl}`,
      foundItLink: link.AnchorText,
    });

    const navigate = () => {
      router.push(
        {
          pathname: navigation.pathname,
          query: navigation.trackedQueryParams,
        },
        navigation.trackedHref,
      );
    };

    if (isRequestAnimationFrameOn?.value) {
      requestAnimationFrame(navigate);
    } else {
      navigate();
    }
  };

  const carouselProps = {
    small: 2,
    medium: 3,
    large: 4,
    ...(configProps?.carousel || {}),
  };

  return (
    <div data-component="FoundItCarousel" className={styles.links}>
      <Carousel {...carouselProps}>
        {links.map((link, i) => {
          const { AnchorText, ImageUrl } = link;
          const navigation = buildFoundItNavigation(link);

          return (
            <div
              key={i}
              className={classNames(styles.link, {
                [styles.link__first!]: i === 0,
                [styles.link__last!]: i === links.length - 1,
                [styles.link__middle!]:
                  fullWidth && i == 5 && links.length == 12,
              })}
            >
              <UnderlinedButton
                className={classNames(styles.externalButton, {
                  [styles.grey!]: greyBackground,
                })}
                tag="a"
                href={navigation.trackedHref}
                rel="nofollow"
                onClick={(evt: React.MouseEvent<Element>) =>
                  onFoundItClick(evt, link)
                }
              >
                {ImageUrl && (
                  <img src={ImageUrl} alt={`Click to view ${AnchorText}`} />
                )}
                <span>{AnchorText}</span>
              </UnderlinedButton>
            </div>
          );
        })}
      </Carousel>
    </div>
  );
}
